import { Link } from "react-router";
import { Adjuntos, Alerta, Badge, Button, Card, DataList, Dialog, MetricCard, MontoField, Row, Select, Spinner, Stack, TextArea, TextField, VisorArchivo } from "@/components";
import type { FacturaVinculada, LineaCotizacion } from "@/lib/api";
import { ETIQUETA_ESTADO, textoComposicion, textoPropuesta, TONO_ESTADO } from "@/lib/cotizaciones";
import { faltaPagar } from "@/lib/facturas";
import { formatPorcentajeCuota } from "@/lib/formaDePago";
import { formatFecha, formatMoneda } from "@/lib/format";
import { ETIQUETA_ESTADO as ESTADO_FACTURA, TONO_ESTADO as TONO_FACTURA } from "@/lib/gastos";
import { formatCantidad } from "@/lib/presupuesto";
import { FormaDePago } from "./FormaDePago";
import { useCotizacionTab } from "./useCotizacionTab";

const claseLink = "text-primario underline-offset-2 hover:underline";

export function CotizacionTab() {
  const t = useCotizacionTab();
  const c = t.detalle;
  const principal = c?.adjuntos[0];
  const dlg = t.dialogo;
  const plata = (v: string | null) => (v === null ? "—" : formatMoneda(v, t.moneda));

  const pagoDelPlan = (f: FacturaVinculada) => c?.cuotas.find((x) => x.id === f.cuota_id)?.concepto ?? "Sin pago del plan";
  const columnasFacturas = [
    { clave: "fecha", titulo: "Fecha", render: (f: FacturaVinculada) => formatFecha(f.fecha) },
    {
      clave: "descripcion",
      titulo: "Factura",
      render: (f: FacturaVinculada) => (
        <Link to={`/gastos/${f.id}`} className={claseLink}>
          {f.comprobante_nro ? `${f.descripcion} · ${f.comprobante_nro}` : f.descripcion}
        </Link>
      ),
    },
    { clave: "cuota", titulo: "Pago del plan", render: pagoDelPlan },
    {
      clave: "monto",
      titulo: "Monto",
      render: (f: FacturaVinculada) => (
        <span>
          {formatMoneda(f.monto, f.moneda)}
          {f.moneda === "ARS" && <span className="block text-chico text-texto-suave">{formatMoneda(f.monto_usd, "USD")}</span>}
        </span>
      ),
    },
    { clave: "estado", titulo: "Estado", render: (f: FacturaVinculada) => <Badge tono={TONO_FACTURA[f.estado]}>{ESTADO_FACTURA[f.estado]}</Badge> },
  ];

  const columnas = [
    { clave: "descripcion", titulo: "Descripción", render: (l: LineaCotizacion) => <span className="whitespace-pre-line">{l.descripcion}</span> },
    { clave: "cantidad", titulo: "Cantidad", render: (l: LineaCotizacion) => formatCantidad(l) },
    { clave: "precio", titulo: "Precio unitario", render: (l: LineaCotizacion) => plata(l.precio_unitario) },
    { clave: "subtotal", titulo: "Subtotal", render: (l: LineaCotizacion) => plata(l.subtotal) },
    { clave: "item", titulo: "Ítem del presupuesto", render: (l: LineaCotizacion) => t.nombreItem(l.presupuesto_item_id) ?? <span className="text-texto-suave">Sin vincular</span> },
  ];

  const acciones = t.esAdmin
    ? (l: LineaCotizacion) => (
        <Row espacio={2}>
          <Button variante="secundario" tamanio="sm" aria-label={`Editar ${l.descripcion}`} onClick={() => t.abrirEditar(l)}>
            Editar
          </Button>
          <Button variante="fantasma" tamanio="sm" aria-label={`Sacar ${l.descripcion}`} onClick={() => t.abrirBorrar(l)}>
            Sacar
          </Button>
        </Row>
      )
    : undefined;

  return (
    <Stack espacio={6}>
      <Link to="/cotizaciones" className={claseLink}>
        ‹ Presupuestos
      </Link>

      {t.cargando && (
        <div className="grid place-items-center py-10 text-primario">
          <Spinner />
        </div>
      )}
      {t.error && <Alerta texto={t.error} />}

      {c && (
        <>
          <Row justificar="entre" envolver espacio={3}>
            <Row envolver espacio={2}>
              <h1 className="text-pantalla font-light tracking-[-0.03em]">{c.proveedor}</h1>
              <Badge>{t.subcategoria}</Badge>
              <Badge tono={TONO_ESTADO[c.estado]}>{ETIQUETA_ESTADO[c.estado]}</Badge>
              {t.vencida && <Badge tono="peligro">Vencido</Badge>}
            </Row>
            {t.esAdmin && (
              <Row envolver espacio={3}>
                <Link to={t.rutaEditar} className={claseLink}>
                  Editar datos
                </Link>
                {t.puedeAceptar && <Button onClick={t.abrirAceptar}>Aceptar presupuesto</Button>}
              </Row>
            )}
          </Row>

          {t.esAdmin && (
            <Stack espacio={2}>
              <Select
                etiqueta="Subcategoría"
                opciones={t.opcionesSubcategoria}
                valor={c.item_id === null ? undefined : String(c.item_id)}
                onCambio={(v) => void t.cambiarSubcategoria(v)}
                placeholder="Escribí el número, el nombre o la categoría"
                deshabilitado={t.guardandoSubcategoria}
                className="max-w-md"
              />
              {c.item_id === null && <Alerta tono="alerta" texto={`Es de antes de las subcategorías${c.etiqueta ? ` (etiqueta «${c.etiqueta}»)` : ""}: elegí su subcategoría para compararlo y aceptarlo con los demás de ella.`} />}
              <Alerta texto={t.errorSubcategoria} />
            </Stack>
          )}

          {principal ? (
            <Card>
              <Stack espacio={2}>
                <p className="font-medium">Presupuesto original</p>
                <VisorArchivo nombre={principal.nombre} tipoMime={principal.tipo_mime} rutaVer={t.rutaVer(principal)} rutaDescarga={t.rutaDescarga(principal)} />
                {c.adjuntos.slice(1).map((a) => (
                  <a key={a.id} href={t.rutaDescarga(a)} className={`${claseLink} truncate`} download>
                    {`Descargar «${a.nombre}»`}
                  </a>
                ))}
              </Stack>
            </Card>
          ) : (
            t.esAdmin && <p className="text-chico text-texto-suave">No tiene adjunto el PDF o la foto del presupuesto: subilo en «Archivos del presupuesto», abajo de todo.</p>
          )}

          <div className="grid gap-3 sm:grid-cols-3">
            <MetricCard
              titulo="Total del presupuesto"
              valor={c.monto === null || c.moneda === null ? "Sin monto" : formatMoneda(c.monto, c.moneda)}
              detalle={c.moneda === "ARS" && c.monto_usd !== null ? formatMoneda(c.monto_usd, "USD") : "Cargado a mano, como figura en el presupuesto"}
            />
            <MetricCard titulo="Suma de las líneas" valor={plata(c.suma_lineas)} detalle={`${c.lineas.length} ${c.lineas.length === 1 ? "línea" : "líneas"}`} />
            <MetricCard titulo="Fecha" valor={formatFecha(c.fecha)} detalle={c.valido_hasta ? `Válido hasta ${formatFecha(c.valido_hasta)}` : "Sin fecha de validez"} />
          </div>

          <Card>
            <Stack espacio={1}>
              <p>
                <span className="font-medium">Cubre: </span>
                {textoComposicion(c)}
              </p>
              {textoPropuesta(c) && <p className="text-chico text-texto-suave">{textoPropuesta(c)}</p>}
            </Stack>
          </Card>

          {(c.descripcion || c.notas) && (
            <Card>
              <Stack espacio={2}>
                {c.descripcion && <p className="font-medium">{c.descripcion}</p>}
                {c.notas && <p className="whitespace-pre-line text-texto-suave">{c.notas}</p>}
              </Stack>
            </Card>
          )}

          <Stack espacio={3}>
            <Row justificar="entre" envolver espacio={2}>
              <h2 className="text-titulo font-light tracking-tight">Líneas</h2>
              {t.esAdmin && <Button onClick={t.abrirNueva}>Agregar línea</Button>}
            </Row>
            <Alerta tono="alerta" texto={t.avisoDiferencia} />
            {t.esAdmin && t.sinVinculables && c.item_id === null && (
              <p className="text-chico text-texto-suave">Para vincular las líneas a los ítems del presupuesto de obra, elegí su subcategoría.</p>
            )}
            <DataList etiqueta={`Líneas del presupuesto de ${c.proveedor}`} columnas={columnas} filas={c.lineas} claveFila={(l) => l.id} acciones={acciones} vacio="Este presupuesto todavía no tiene líneas." />
          </Stack>

          <FormaDePago base={t.base} detalle={c} recargar={t.recargar} esAdmin={t.esAdmin} />

          <Stack espacio={3}>
            <Row justificar="entre" envolver espacio={2}>
              <h2 className="text-titulo font-light tracking-tight">Facturas</h2>
              {c.facturas.length > 0 && (
                <Link to={`/gastos?presupuesto=${c.id}`} className={claseLink}>
                  Ver en Gastos
                </Link>
              )}
            </Row>
            {(c.facturas.length > 0 || c.estado === "aceptado") && (
              <div className="grid gap-3 sm:grid-cols-3">
                <MetricCard titulo="Facturado" valor={formatMoneda(c.facturado_usd, "USD")} detalle={`${c.facturas.length} ${c.facturas.length === 1 ? "factura" : "facturas"}`} />
                <MetricCard titulo="Pagado" valor={formatMoneda(c.pagado_usd, "USD")} detalle="Al cambio de cada factura" />
                <MetricCard titulo="Falta pagar" valor={faltaPagar(c)} detalle={c.monto_usd === null ? undefined : `De ${formatMoneda(c.monto_usd, "USD")}`} />
              </div>
            )}
            <DataList etiqueta={`Facturas del presupuesto de ${c.proveedor}`} columnas={columnasFacturas} filas={c.facturas} claveFila={(f) => f.id} vacio="Todavía no hay facturas de este presupuesto: se vinculan al cargarlas en Gastos." />
          </Stack>

          <Adjuntos etiqueta="Archivos del presupuesto" adjuntos={c.adjuntos} rutaDescarga={t.rutaDescarga} puedeEditar={t.esAdmin} onSubir={t.subirAdjunto} onBorrar={t.borrarAdjunto} subiendo={t.subiendo} error={t.errorAdjuntos} />
        </>
      )}

      <Dialog abierto={dlg?.tipo === "linea"} onCambio={(abierto) => !abierto && t.cerrar()} titulo={dlg?.tipo === "linea" && dlg.linea ? "Editar línea" : "Agregar línea"}>
        {dlg?.tipo === "linea" && (
          <form onSubmit={(e) => void t.guardar(e)}>
            <Stack espacio={4}>
              <TextArea etiqueta="Descripción" filas={3} value={t.form.descripcion} onChange={(e) => t.cambiar("descripcion", e.target.value)} error={t.errores.descripcion} />
              <div className="grid gap-3 sm:grid-cols-2">
                <Select
                  etiqueta="Unidad"
                  opciones={t.opcionesUnidad}
                  valor={t.form.unidad}
                  onCambio={(v) => t.cambiar("unidad", v)}
                  onCrear={(texto) => t.cambiar("unidad", texto.toLowerCase())}
                  placeholder="gl, m2, un…"
                  error={t.errores.unidad}
                />
                <TextField etiqueta="Cantidad" inputMode="decimal" value={t.form.cantidad} onChange={(e) => t.cambiar("cantidad", e.target.value)} error={t.errores.cantidad} />
              </div>
              <MontoField etiqueta="Precio unitario" valor={t.form.precio} onCambio={(v) => t.cambiar("precio", v)} moneda={t.moneda} error={t.errores.precio} />
              <p className="text-texto-suave">Subtotal: {plata(t.subtotal)}</p>
              <Select etiqueta="Ítem del presupuesto de obra" opciones={t.opcionesItem} valor={t.form.item} onCambio={(v) => t.cambiar("item", v)} />
              <Alerta texto={t.errorGuardar} />
              <Row justificar="fin" espacio={2}>
                <Button type="button" variante="secundario" onClick={t.cerrar}>
                  Cancelar
                </Button>
                <Button type="submit" cargando={t.guardando}>
                  Guardar
                </Button>
              </Row>
            </Stack>
          </form>
        )}
      </Dialog>

      <Dialog abierto={dlg?.tipo === "aceptar"} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Aceptar presupuesto">
        {dlg?.tipo === "aceptar" && c && (
          <Stack espacio={4}>
            <Stack espacio={1}>
              <p className="font-medium">{`${c.proveedor} · ${t.subcategoria}`}</p>
              <p>{c.monto !== null && c.moneda !== null ? formatMoneda(c.monto, c.moneda) : "Sin monto"}{c.moneda === "ARS" && c.monto_usd !== null ? ` (${formatMoneda(c.monto_usd, "USD")})` : ""}</p>
              <p className="text-chico text-texto-suave">{`Cubre: ${textoComposicion(c)}`}</p>
              <p className="text-chico text-texto-suave">
                {c.cuotas.length === 0 ? "Sin forma de pago" : `Forma de pago: ${c.cuotas.map((x) => `${x.concepto} ${formatPorcentajeCuota(x.porcentaje)}`).join(" · ")}`}
              </p>
            </Stack>
            {t.avisosAceptar.length > 0 && (
              <Stack espacio={2}>
                <p className="font-medium">Antes de aceptar, revisá:</p>
                {t.avisosAceptar.map((a) => (
                  <Alerta key={a} tono="alerta" texto={a} />
                ))}
              </Stack>
            )}
            {t.quedanNoElegidos === null ? (
              !t.errorAceptar && (
                <div className="text-primario">
                  <Spinner />
                </div>
              )
            ) : t.quedanNoElegidos.length > 0 ? (
              <Stack espacio={1}>
                <p>{`Al aceptarlo, ${t.quedanNoElegidos.length === 1 ? "este queda" : "estos quedan"} como no elegidos:`}</p>
                <ul aria-label="Quedan como no elegidos" className="flex flex-col gap-1 text-chico">
                  {t.quedanNoElegidos.map((o) => (
                    <li key={o.id}>{`${o.proveedor} · ${o.monto !== null && o.moneda !== null ? formatMoneda(o.monto, o.moneda) : "sin monto"} · ${ETIQUETA_ESTADO[o.estado]}`}</li>
                  ))}
                </ul>
              </Stack>
            ) : (
              <p className="text-chico text-texto-suave">{`No hay otros presupuestos de «${t.subcategoria}».`}</p>
            )}
            <Alerta texto={t.errorAceptar} />
            <Row justificar="fin" espacio={2}>
              <Button variante="secundario" onClick={t.cerrar}>
                Cancelar
              </Button>
              <Button cargando={t.aceptando} disabled={t.quedanNoElegidos === null} onClick={() => void t.confirmarAceptar()}>
                Aceptar presupuesto
              </Button>
            </Row>
          </Stack>
        )}
      </Dialog>

      <Dialog abierto={dlg?.tipo === "borrar"} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Sacar línea">
        {dlg?.tipo === "borrar" && (
          <Stack espacio={4}>
            <p>{`¿Sacar "${dlg.linea.descripcion}" del presupuesto?`}</p>
            <Alerta texto={t.errorBorrar} />
            <Row justificar="fin" espacio={2}>
              <Button variante="secundario" onClick={t.cerrar}>
                Cancelar
              </Button>
              <Button variante="peligro" cargando={t.borrando} onClick={() => void t.confirmarBorrar()}>
                Sacar
              </Button>
            </Row>
          </Stack>
        )}
      </Dialog>
    </Stack>
  );
}
